<template>
  <DashboardLayout :lastUpdated="lastUpdated">
    <!-- Navbar filters slot -->
    <template slot="navbarFilters">
      <div v-if="hasStockData" class="navbar-filters">
        <div
          v-if="sellerAccountsList.length > 0"
          class="ui dropdown filter-dropdown navbar-dropdown"
          ref="saDropdown"
        >
          <div class="dropdown-trigger">
            <span class="text">S.Accounts: {{ filterBySellAcc.length === 0 ? 'All' : filterBySellAcc.length + ' selected' }}</span>
            <i class="dropdown icon"></i>
          </div>
          <div class="menu">
            <div class="item" :class="{ selected: filterBySellAcc.length === 0 }" data-value="">
              <i class="check icon" v-if="filterBySellAcc.length === 0"></i>
              All
            </div>
            <div class="divider"></div>
            <div
              v-for="sa in sellerAccountsList"
              :key="String(sa.sellerId)"
              class="item"
              :class="{ selected: selectedSellerIdSet.has(String(sa.sellerId)) }"
              :data-value="sa.sellerId"
            >
              <i class="check icon" v-if="selectedSellerIdSet.has(String(sa.sellerId))"></i>
              {{ sa.accountNickName }}
            </div>
          </div>
        </div>

        <div
          v-if="filterUserCountries.length > 0"
          class="ui dropdown filter-dropdown navbar-dropdown"
          ref="marketplaceDropdown"
        >
          <div class="dropdown-trigger">
            <span class="text">Marketplaces: {{ filterByMarket.length === 0 ? 'All' : filterByMarket.length === 1 ? filterByMarket[0] : filterByMarket.length + ' selected' }}</span>
            <i class="dropdown icon"></i>
          </div>
          <div class="menu">
            <div class="item" :class="{ selected: filterByMarket.length === 0 }" data-value="">
              <i class="check icon" v-if="filterByMarket.length === 0"></i>
              All
            </div>
            <div class="divider"></div>
            <div
              v-for="c in filteredCountries"
              :key="c"
              :class="[c === 's' ? 'divider' : 'item', { selected: filterByMarket.indexOf(c) >= 0 && c !== 's' }]"
              :data-value="c"
            >
              <i class="check icon" v-if="filterByMarket.indexOf(c) >= 0 && c !== 's'"></i>
              <i v-if="c !== 's'" :class="c.toLowerCase().replace('uk', 'gb') + ' flag'" style="margin-right: 8px;"></i>
              <span v-if="c !== 's'">Amazon.{{ mpSuffix(c) }}</span>
            </div>
          </div>
        </div>
      </div>
    </template>

    <div class="stock-dashboard">
      <div class="stock-dashboard-content">

        <!-- Page Header -->
        <div class="stock-page-header stock-page-header-with-actions">
          <div class="stock-page-header-text">
            <h1 class="stock-page-title">Stock Dashboard</h1>
            <p class="stock-page-subtitle">Monitor FBA &amp; FBM inventory levels across all products</p>
          </div>
          <div v-if="hasStockData" class="stock-page-header-actions">
            <button
              type="button"
              class="stock-thresholds-btn"
              :class="{ open: thresholdPanelOpen }"
              @click="toggleThresholdPanel"
            >
              <i class="setting icon" />
              Alert Thresholds
              <i class="angle down icon stock-thresholds-chevron" />
            </button>
            <button type="button" class="stock-export-btn" @click="exportCSV">
              <i class="download icon" />
              Export CSV
            </button>
          </div>
        </div>

        <!-- Alert Thresholds Panel -->
        <div
          v-if="hasStockData && thresholdPanelOpen && !thresholdPanelReady"
          class="stock-thresholds-prefetch-bar"
          role="status"
          aria-live="polite"
        >
          <i class="notched circle loading icon" />
          <span>Loading alert thresholds…</span>
        </div>
        <div
          v-show="hasStockData && thresholdPanelOpen"
          :class="{ 'stock-thresholds-panel-wrap--prefetch': !thresholdPanelReady }"
        >
          <StockAlertThresholdsPanel
            ref="thresholdsPanel"
            :panel-open="hasStockData && thresholdPanelOpen"
            :seller-account-id="thresholdSellerAccountId"
            @close="onThresholdClose"
            @saved="onThresholdSaved"
            @spreadsheet-imported="onThresholdSaved"
            @thresholds-load-complete="thresholdPanelReady = true"
          />
        </div>

        <!-- Stat Cards -->
        <StockStatCards
          :stats="statsData"
          :active-filter="activeFilter"
          :marketplace-count="marketplaceCount"
          @filter-change="onFilterChange"
        />

        <!-- Inventory Table -->
        <StockInventoryTable
          :products="products"
          :sort-key="tableSortKey"
          :sort-dir="tableSortDir"
          :search-query="searchQuery"
          :current-page="currentPage"
          :total-pages="totalPages"
          :total-items="totalItems"
          :pagination-pages="paginationPages"
          @sort="onSort"
          @search="onSearch"
          @page="onPage"
          @show-details="onShowDetails"
        />

      </div>
    </div>
  </DashboardLayout>
</template>

<script>
import $ from 'jquery'
import {
  extractV1SellersItems,
  mapV1SellersToFilterAccounts,
  buildMarketplaceFilterCodes,
  buildMarketplaceFilterDropdownItems,
  MARKETPLACE_FILTER_COUNTRIES_ALL,
  getMarketplaceAmazonSuffix
} from './utils/sellerAccountsFromV1Api.js'
import './components/stock/styles/StockDashboard.css'
import DashboardLayout from './components/buybox/DashboardLayout.vue'
import StockAlertThresholdsPanel from './components/stock/StockAlertThresholdsPanel.vue'
import StockStatCards from './components/stock/StockStatCards.vue'
import StockInventoryTable from './components/stock/StockInventoryTable.vue'

const STOCK_URL_KEYS = ['status', 'q', 'marketplaces', 'sellerAccounts', 'page', 'sort', 'order']
const STOCK_TABLE_SORT_KEYS = ['fbaStock', 'fbmStock', 'velocity', 'daysLeft', 'status']

function shallowEqualStockQuery (next, routeQuery) {
  for (var i = 0; i < STOCK_URL_KEYS.length; i++) {
    var k = STOCK_URL_KEYS[i]
    var nv = next[k] != null ? String(next[k]) : ''
    var rv = routeQuery && routeQuery[k] != null ? String(routeQuery[k]) : ''
    if (nv !== rv) return false
  }
  return true
}

export default {
  name: 'StockPage',
  components: { DashboardLayout, StockAlertThresholdsPanel, StockStatCards, StockInventoryTable },

  data: function () {
    return {
      // Threshold panel
      thresholdPanelOpen: false,
      thresholdPanelReady: false,
      // Filters
      activeFilter: 'all',
      searchQuery: '',
      searchTimeout: null,
      filterByMarket: [],
      filterBySellAcc: [],
      filterCountriesAll: MARKETPLACE_FILTER_COUNTRIES_ALL,
      filterUserCountries: [],
      stockFilterData: { availableFilters: null },
      loadingFilters: false,
      // Sort
      tableSortKey: 'fbaStock',
      tableSortDir: 'desc',
      // Timer
      lastUpdated: 'just now',
      lastUpdateTime: null,
      timerInterval: null,
      // Internal flags
      _syncingDropdowns: false,
      _applyingStockRouteQuery: false
    }
  },

  computed: {
    worldStore: function () {
      return this.$parent && this.$parent.worldStore
    },
    hasStockData: function () {
      return !!(this.worldStore && this.worldStore.products !== undefined)
    },

    // ── Filters ───────────────────────────────────────────────────────────
    selectedSellerIdSet: function () {
      var s = new Set()
      ;(this.filterBySellAcc || []).forEach(function (id) { s.add(String(id)) })
      return s
    },
    sellerAccountsList: function () {
      var af = this.stockFilterData && this.stockFilterData.availableFilters
      if (!af || !af.sellerAccounts || af.sellerAccounts.length === 0) return []
      return af.sellerAccounts.map(function (sa) {
        return { sellerId: sa.id, accountNickName: sa.name }
      })
    },
    filteredCountries: function () {
      return buildMarketplaceFilterDropdownItems(this.filterUserCountries, this.filterCountriesAll)
    },

    // ── Products ──────────────────────────────────────────────────────────
    products: function () {
      if (!this.worldStore || !this.worldStore.products) return []
      var ids = (this.$parent && this.$parent.stockPageProductIds) || []
      var out = []
      for (var i = 0; i < ids.length; i++) {
        var p = this.worldStore.products[ids[i]]
        if (!p) continue
        var fulfillable = p.fbaAvailable != null ? p.fbaAvailable : 0
        var reserved = p.reservedStock != null ? p.reservedStock : 0
        var pct = p.trend
        var trendText, trendCls
        if (pct !== null && pct !== undefined) {
          trendCls = pct > 0 ? 'up' : (pct < 0 ? 'down' : 'neutral')
          trendText = pct > 0
            ? ('↑ +' + pct + '% vs last wk')
            : (pct < 0 ? ('↓ ' + pct + '% vs last wk') : '— 0% vs last wk')
        } else {
          trendCls = 'neutral'
          trendText = ''
        }
        out.push(Object.assign({}, p, {
          _fulfillable: fulfillable,
          _reserved: reserved,
          _fbaTotal: fulfillable + reserved,
          _isTransferring: fulfillable === 0 && reserved > 0,
          _trendText: trendText,
          _trendCls: trendCls
        }))
      }
      return out
    },

    // ── Summary ───────────────────────────────────────────────────────────
    statsData: function () {
      var s = this.$parent && this.$parent.stocksSummary
      return {
        totalProducts: (s && s.totalProducts) || 0,
        healthyStock: (s && s.healthyStock) || 0,
        lowStockWarning: (s && s.lowStockWarning) || 0,
        outOfStock: (s && s.outOfStock) || 0,
        healthyPercentage: (s && s.healthyPercentage) || 0
      }
    },
    marketplaceCount: function () {
      var seen = {}
      this.products.forEach(function (p) { if (p.marketplace) seen[p.marketplace] = true })
      return Object.keys(seen).length
    },

    // ── Pagination ────────────────────────────────────────────────────────
    currentPage: function () { return this.$parent ? (this.$parent.stocksCurrentPage || 0) : 0 },
    totalPages: function () { return this.$parent ? (this.$parent.stocksTotalPages || 0) : 0 },
    totalItems: function () { return this.$parent ? (this.$parent.stocksTotalItems || 0) : 0 },
    paginationPages: function () {
      var total = this.totalPages
      var current = this.currentPage
      var start, end
      if (total <= 5) {
        start = 0; end = total - 1
      } else {
        start = Math.max(0, current - 2)
        end = Math.min(total - 1, start + 4)
        start = Math.max(0, end - 4)
      }
      var pages = []
      for (var i = start; i <= end; i++) pages.push(i)
      return pages
    },

    // ── Threshold panel scope ─────────────────────────────────────────────
    thresholdSellerAccountId: function () {
      return this.filterBySellAcc.length === 1 ? String(this.filterBySellAcc[0]) : null
    }
  },

  watch: {
    '$route': function (to, from) {
      if (to.path !== '/stock') return
      if (this._applyingStockRouteQuery) return
      if (from && from.path === to.path && shallowEqualStockQuery(this.buildRouteQuery(), to.query)) return
      this.applyRouteQuery(to.query)
      this.loadPage(this.currentPage)
    },
    filterUserCountries: function () {
      this.$nextTick(this.initDropdowns)
    },
    sellerAccountsList: function () {
      this.$nextTick(this.initDropdowns)
    }
  },

  created: function () {
    this.applyRouteQuery(this.$route.query)
  },
  mounted: function () {
    this.startTimer()
    this.loadFilters()
    this.loadPage(this.currentPage)
    this.$nextTick(this.initDropdowns)
    this.$nextTick(this.syncRouteQuery)
  },
  beforeDestroy: function () {
    clearInterval(this.timerInterval)
    clearTimeout(this.searchTimeout)
  },

  methods: {

    // ── Child event handlers ───────────────────────────────────────────────
    onFilterChange: function (filter) {
      this.activeFilter = filter
      this.fetchData()
    },
    onSort: function (column) {
      if (STOCK_TABLE_SORT_KEYS.indexOf(column) === -1) return
      if (this.tableSortKey !== column) {
        this.tableSortKey = column
        this.tableSortDir = column === 'status' ? 'asc' : 'desc'
      } else {
        this.tableSortDir = this.tableSortDir === 'desc' ? 'asc' : 'desc'
      }
      if (this.$parent) this.$parent.stocksCurrentPage = 0
      this.loadPage(0, true)
    },
    onSearch: function (query) {
      this.searchQuery = query
      clearTimeout(this.searchTimeout)
      var self = this
      this.searchTimeout = setTimeout(function () { self.fetchData() }, 500)
    },
    onPage: function (page) {
      var p = Number(page)
      if (p < 0 || p >= this.totalPages) return
      this.loadPage(p, true)
    },
    onShowDetails: function (productId) {
      if (this.$parent && this.$parent.$refs && this.$parent.$refs.chartModal) {
        var product = this.worldStore.products[productId]
        if (product) this.$parent.$refs.chartModal.open(product, null)
      }
    },

    // ── Threshold panel ────────────────────────────────────────────────────
    toggleThresholdPanel: function () {
      if (this.thresholdPanelOpen) {
        this.thresholdPanelOpen = false
        this.thresholdPanelReady = false
      } else if (this.hasStockData) {
        this.thresholdPanelReady = false
        this.thresholdPanelOpen = true
      }
    },
    onThresholdClose: function () {
      this.thresholdPanelOpen = false
      this.thresholdPanelReady = false
    },
    onThresholdSaved: function () {
      this.refreshSummary()
      this.loadPage(this.currentPage, true)
    },

    // ── Data fetching ──────────────────────────────────────────────────────
    fetchArgs: function (page, skipSummary) {
      return [
        page,
        this.$parent.stocksPageSize,
        this.searchQuery.trim() || null,
        this.activeFilter === 'all' ? null : this.activeFilter,
        this.filterByMarket.length > 0 ? this.filterByMarket.join(',') : null,
        this.filterBySellAcc.length > 0 ? this.filterBySellAcc.join(',') : null,
        this.tableSortKey,
        this.tableSortDir,
        !!skipSummary
      ]
    },
    fetchData: function () {
      if (!this.$parent.stockData) return
      this.$parent.stocksCurrentPage = 0
      if (this.activeFilter !== 'all') this.refreshSummary()
      this.$parent.stockData.apply(this.$parent, this.fetchArgs(0))
      this.syncRouteQuery()
    },
    loadPage: function (page, skipSummary) {
      if (!this.$parent.stockData) return
      this.$parent.stockData.apply(this.$parent, this.fetchArgs(page, skipSummary))
      this.syncRouteQuery()
    },
    refreshSummary: function () {
      if (!this.$parent.stockSummaryData) return
      this.$parent.stockSummaryData(
        this.filterByMarket.length > 0 ? this.filterByMarket.join(',') : null,
        this.filterBySellAcc.length > 0 ? this.filterBySellAcc.join(',') : null
      )
    },

    // ── Route sync ─────────────────────────────────────────────────────────
    buildRouteQuery: function () {
      var o = {}
      if (this.activeFilter !== 'all') o.status = this.activeFilter
      if (this.searchQuery.trim()) o.q = this.searchQuery.trim()
      if (this.filterByMarket.length) o.marketplaces = this.filterByMarket.join(',')
      if (this.filterBySellAcc.length) o.sellerAccounts = this.filterBySellAcc.map(String).join(',')
      if (this.currentPage > 0) o.page = String(this.currentPage)
      if (this.tableSortKey !== 'fbaStock') o.sort = this.tableSortKey
      if (this.tableSortDir !== 'desc') o.order = this.tableSortDir
      return o
    },
    syncRouteQuery: function () {
      if (this._applyingStockRouteQuery || this.$route.path !== '/stock') return
      var next = this.buildRouteQuery()
      if (shallowEqualStockQuery(next, this.$route.query)) return
      this.$router.replace({ path: '/stock', query: next })
    },
    applyRouteQuery: function (q) {
      if (!q || typeof q !== 'object') return false
      var has = STOCK_URL_KEYS.some(function (k) { return q[k] != null && q[k] !== '' })
      if (!has) return false
      this._applyingStockRouteQuery = true
      try {
        this.activeFilter = (q.status && ['all', 'healthy', 'low', 'critical'].indexOf(q.status) >= 0) ? q.status : 'all'
        this.searchQuery = q.q != null ? String(q.q) : ''
        this.filterByMarket = q.marketplaces ? String(q.marketplaces).split(',').map(function (s) { return s.trim() }).filter(Boolean) : []
        this.filterBySellAcc = q.sellerAccounts ? String(q.sellerAccounts).split(',').map(function (s) {
          var t = s.trim(); var n = Number(t); return isNaN(n) ? t : n
        }).filter(Boolean) : []
        this.tableSortKey = (q.sort && STOCK_TABLE_SORT_KEYS.indexOf(String(q.sort)) >= 0) ? String(q.sort) : 'fbaStock'
        this.tableSortDir = (q.order === 'asc' || q.order === 'desc') ? q.order : 'desc'
        if (this.$parent && q.page != null && q.page !== '') {
          var pn = parseInt(q.page, 10)
          if (!isNaN(pn) && pn >= 0) this.$parent.stocksCurrentPage = pn
        }
      } finally {
        var self = this
        this.$nextTick(function () { self._applyingStockRouteQuery = false })
      }
      return true
    },

    // ── Filter dropdowns (jQuery Semantic UI) ─────────────────────────────
    initDropdowns: function () {
      var self = this
      if (this.$refs.saDropdown) {
        try { $(this.$refs.saDropdown).dropdown('destroy') } catch (e) {}
        $(this.$refs.saDropdown).dropdown({
          onChange: function (v) {
            if (self._syncingDropdowns) return
            self.filterBySellAcc = v === '' ? [] : v.split(',').map(Number)
            self.fetchData()
          },
          placeholder: 'All Accounts',
          fullTextSearch: true
        })
      }
      if (this.$refs.marketplaceDropdown) {
        try { $(this.$refs.marketplaceDropdown).dropdown('destroy') } catch (e) {}
        $(this.$refs.marketplaceDropdown).dropdown({
          onChange: function (v) {
            if (self._syncingDropdowns) return
            self.filterByMarket = v === '' ? [] : v.split(',')
            self.fetchData()
          },
          placeholder: 'All Marketplaces',
          fullTextSearch: true
        })
      }
    },

    // ── Filter options loader ──────────────────────────────────────────────
    loadFilters: function () {
      var self = this
      this.loadingFilters = true
      Promise.all([
        self.$http.get('/api/sentinel/products', { params: { productsOnly: true, page: 0, pageSize: 1 } }),
        self.$http.get('/api/v1/sellers').catch(function () { return { body: null } })
      ]).then(function (results) {
        self.loadingFilters = false
        var body = (results[0] && results[0].body) || {}
        var sellersBody = (results[1] && results[1].body) || {}
        var fromV1 = mapV1SellersToFilterAccounts(extractV1SellersItems(sellersBody))
        var fromProducts = (body.availableFilters && body.availableFilters.sellerAccounts) || []
        var sellerAccounts = fromV1.length > 0
          ? fromV1.map(function (r) { return { id: r.id, name: r.name } })
          : fromProducts.map(function (sa) { return { id: sa.id, name: sa.name || ('Account ' + sa.id) } })
        var marketplaces = (body.availableFilters && body.availableFilters.marketplaces) || []
        self.filterUserCountries = buildMarketplaceFilterCodes({
          fromAvailableFilters: marketplaces,
          v1SellersBody: sellersBody,
          canonicalOrder: self.filterCountriesAll.filter(function (c) { return c !== 's' })
        })
        if (self.filterByMarket.length > 0) {
          var mpSet = new Set(self.filterUserCountries)
          self.filterByMarket = self.filterByMarket.filter(function (x) { return mpSet.has(x) })
        }
        self.stockFilterData.availableFilters = { marketplaces: marketplaces, sellerAccounts: sellerAccounts }
        if (sellerAccounts.length > 0 && self.filterBySellAcc.length > 0) {
          var idSet = new Set(sellerAccounts.map(function (sa) { return String(sa.id) }))
          self.filterBySellAcc = self.filterBySellAcc.filter(function (id) { return idSet.has(String(id)) })
        }
        self.$nextTick(self.initDropdowns)
      }, function () {
        self.loadingFilters = false
        self.stockFilterData.availableFilters = null
      })
    },

    // ── Timer ──────────────────────────────────────────────────────────────
    startTimer: function () {
      this.lastUpdateTime = Date.now()
      this.lastUpdated = 'just now'
      var self = this
      this.timerInterval = setInterval(function () {
        var diff = Math.floor((Date.now() - self.lastUpdateTime) / 1000)
        if (diff < 60) self.lastUpdated = diff + 's ago'
        else if (diff < 3600) self.lastUpdated = Math.floor(diff / 60) + 'm ago'
        else self.lastUpdated = Math.floor(diff / 3600) + 'h ago'
      }, 1000)
    },

    // ── Helpers ────────────────────────────────────────────────────────────
    mpSuffix: function (country) { return getMarketplaceAmazonSuffix(country) },
    exportCSV: function () {
      var rows = [['Product', 'ASIN', 'Marketplace', 'FBA Fulfillable', 'FBA Transferring', 'FBA Reserved', 'FBA Inbound', 'FBM Stock', 'Velocity', 'Days Left', 'Status']]
      this.products.forEach(function (p) {
        rows.push([
          '"' + (p.productTitle || '').replace(/"/g, '""') + '"',
          p.asin || String(p.productId || ''),
          p.marketplace || '',
          p._fulfillable,
          p._reserved,
          p._reserved,
          p.fbaInbound != null ? p.fbaInbound : '',
          p.fbmStock != null ? p.fbmStock : '',
          p.velocity != null ? p.velocity : '',
          p.daysLeft != null ? p.daysLeft : '',
          p.status || 'Unknown'
        ])
      })
      var csv = rows.map(function (r) { return r.join(',') }).join('\n')
      var blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
      var link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = 'stock-inventory-' + new Date().toISOString().slice(0, 10) + '.csv'
      link.click()
    }
  }
}
</script>

<style scoped>
.stock-thresholds-prefetch-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 16px;
  padding: 14px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #1b4b82;
  background: #eef4fb;
  border: 1px solid #c7d9ef;
  border-radius: 10px;
}
.stock-thresholds-prefetch-bar .icon { margin: 0; }

.stock-thresholds-panel-wrap--prefetch {
  position: absolute;
  width: 1px; height: 1px;
  margin: -1px; padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  pointer-events: none;
}
</style>



// WEBPACK FOOTER //
// StockPage.vue?902f03ce